
const USER_FIELD_NAMES = ['generated_by_user', 'updated_by_user', 'last_executed_by'];
const MODULE_FIELD_NAME = 'module';

/**
 * Matches: 8-4-4-4-12 hex digits with hyphens
 * Validates hex ranges: 0-9, a-f, A-F
 */
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a field is a user field by examining its values structure
 * User fields have enum values that are objects with id, username, and email properties
 * 
 * @param {Object} fieldDef - Field definition from schema
 * @returns {boolean} True if field appears to be a user field
 */
function isUserField(fieldDef) {
  if (!fieldDef) return false;

  if (USER_FIELD_NAMES.includes(fieldDef.name)) {
    return true;
  }

  if (fieldDef.kind === 'enum' && Array.isArray(fieldDef.values) && fieldDef.values.length > 0) {
    const firstValue = fieldDef.values[0];
    if (typeof firstValue === 'object' && firstValue?.id && firstValue?.username) {
      return true;
    }
  }

  return false;
}

/**
 * Format a value for query text
 * 
 * @param {any} value - Value to format
 * @param {Object} options - Options object with field and schema
 * @param {string} options.field - Field name (optional, for user field lookup)
 * @param {Object} options.schema - Query schema (optional, for user field lookup)
 * @returns {string} Formatted value string
 */
function formatValue(value, options = {}) {
  const { field, schema } = options;

  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'object' && value !== null && value.date && value.timezone) {
    if (typeof value.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.date)) {
      return `"${value.date}"`;
    }
  }

  if (field && schema && typeof value === 'string' && UUID_V4_REGEX.test(value)) {
    const fieldDef = schema.fields?.find(f => f.name === field);

    if (isUserField(fieldDef) && Array.isArray(fieldDef.values)) {
      const userObj = fieldDef.values.find(u => {
        return typeof u === 'object' && u?.id === value;
      });

      if (userObj?.username) {
        const escaped = userObj.username.replace(/"/g, '\\"');
        return `"${escaped}"`;
      }
    }

    if (field === MODULE_FIELD_NAME && Array.isArray(fieldDef?.values)) {
      const moduleObj = fieldDef.values.find(m => {
        return typeof m === 'object' && m?.id === value;
      });

      if (moduleObj) {
        const displayValue = moduleObj.path || moduleObj.name || moduleObj.label;
        if (displayValue) {
          const escaped = displayValue.replace(/"/g, '\\"');
          return `"${escaped}"`;
        }
      }
    }
  }

  if (typeof value === 'string') {
    const escaped = value.replace(/"/g, '\\"');
    return `"${escaped}"`;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  // Handle arrays
  if (Array.isArray(value)) {
    const formatted = value.map(v => formatValue(v, options)).join(', ');
    return `[${formatted}]`;
  }

  // Fallback: convert to string
  return String(value);
}

/**
 * Convert field name to label (with underscores for spaces)
 * 
 * @param {string} fieldName - Field name (e.g., "generated_by_user")
 * @param {Object} schema - Query schema
 * @returns {string} Label with underscores (e.g., "Created_By")
 */
function convertFieldNameToLabel(fieldName, schema) {
  if (!schema?.fields) return fieldName;
  
  const fieldDef = schema.fields.find(f => f.name === fieldName);
  if (fieldDef?.label) {
    return fieldDef.label.replace(/\s+/g, '_').toLowerCase();
  }
  
  return fieldName;
}

/**
 * Convert AST to query text string
 * 
 * @param {Object|null} ast - Canonical AST (can be null)
 * @param {Object} options - Options (optional)
 * @param {Object} options.schema - Query schema (for user field formatting and label conversion)
 * @returns {string} Query text
 */
export function astToText(ast, options = {}) {
  if (!ast || ast === null) {
    return '';
  }

  // Handle predicate nodes
  if (ast.type === 'predicate') {
    const { field, op, value } = ast;

    // Convert field name to label (with underscores)
    const displayField = convertFieldNameToLabel(field, options.schema);

    // Handle unary operators
    if (op === 'isEmpty' || op === 'isNotEmpty') {
      return `${displayField} ${op}`;
    }

    // Handle binary operators - pass field and schema for user field formatting
    return `${displayField} ${op} ${formatValue(value, { field, schema: options.schema })}`;
  }

  // Handle AND nodes
  if (ast.type === 'and' && ast.items) {
    const parts = ast.items.map(item => {
      const text = astToText(item, options);
      // Add parentheses if item is OR or NOT
      if (item.type === 'or' || item.type === 'not') {
        return `(${text})`;
      }
      return text;
    });
    return parts.join(' AND ');
  }

  // Handle OR nodes
  if (ast.type === 'or' && ast.items) {
    const parts = ast.items.map(item => {
      const text = astToText(item, options);
      // Add parentheses if item is AND or NOT
      if (item.type === 'and' || item.type === 'not') {
        return `(${text})`;
      }
      return text;
    });
    return parts.join(' OR ');
  }

  // Handle NOT nodes
  if (ast.type === 'not' && ast.item) {
    const innerText = astToText(ast.item, options);
    // Add parentheses if inner is AND or OR
    if (ast.item.type === 'and' || ast.item.type === 'or') {
      return `NOT (${innerText})`;
    }
    return `NOT ${innerText}`;
  }

  // Fallback: return empty string
  return '';
}

